import { useEffect, useState } from 'react';
import { Clock, CheckCircle, XCircle, PauseCircle, ExternalLink, FileText, Users } from 'lucide-react';
import api from '../../services/api';

const STATUS = {
    DRAFT:      { label: 'Brouillon', icon: Clock, tone: 'muted' },
    SUBMITTED:  { label: 'Envoyée, en attente d’examen', icon: Clock, tone: 'warn' },
    IN_REVIEW:  { label: 'En cours d’examen par l’équipe', icon: Clock, tone: 'warn' },
    APPROVED:   { label: 'Publiée', icon: CheckCircle, tone: 'ok' },
    REJECTED:   { label: 'Non retenue', icon: XCircle, tone: 'ko' },
    SUSPENDED:  { label: 'Suspendue', icon: PauseCircle, tone: 'muted' },
};

const fmtDate = (s) => {
    if (!s) return null;
    const d = new Date(String(s).replace(' ', 'T'));
    return isNaN(d) ? null : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * État de la publication communautaire d'une thématique perso (remplace l'ancien tableau de bord
 * « Mes publications », conçu pour un partage de revenus abandonné). Affiché en tête de
 * « Gérer les publications » ; rien si la thématique n'a jamais été soumise.
 */
export default function PublicationStatus({ thematiqueId }) {
    const [pub, setPub] = useState(undefined);

    useEffect(() => {
        let alive = true;
        api.get('/partner/publish.php')
            .then(({ data }) => {
                const list = data.data?.publications || data.data?.scopes || data.data || [];
                const mine = (Array.isArray(list) ? list : []).filter(p => Number(p.source_thematique_id) === Number(thematiqueId));
                if (alive) setPub(mine[0] || null);
            })
            .catch(() => { if (alive) setPub(null); });
        return () => { alive = false; };
    }, [thematiqueId]);

    if (!pub) return null;
    const st = STATUS[pub.submission_status] || STATUS.DRAFT;
    const Icon = st.icon;
    const published = fmtDate(pub.published_at);
    const submitted = fmtDate(pub.submitted_at);
    const reviewed = fmtDate(pub.reviewed_at);

    return (
        <section className={`pubstat pubstat--${st.tone}`}>
            <div className="pubstat__head">
                <Icon size={18} />
                <div>
                    <strong>{st.label}</strong>
                    <span>
                        « {pub.label} »
                        {pub.submission_status === 'APPROVED' && published ? ` · depuis le ${published}` : ''}
                        {['SUBMITTED', 'IN_REVIEW'].includes(pub.submission_status) && submitted ? ` · envoyée le ${submitted}` : ''}
                        {pub.submission_status === 'REJECTED' && reviewed ? ` · le ${reviewed}` : ''}
                        {Number(pub.version) > 1 ? ` · version ${pub.version}` : ''}
                    </span>
                </div>
                {pub.submission_status === 'APPROVED' && pub.slug && (
                    <a className="pubstat__link" href={`/${pub.slug}`} target="_blank" rel="noopener noreferrer">
                        Voir la page publique <ExternalLink size={13} />
                    </a>
                )}
            </div>
            {pub.submission_status === 'APPROVED' && (
                <div className="pubstat__stats">
                    <span><FileText size={14} /> {pub.nb_questions ?? 0} questions publiées</span>
                    <span><Users size={14} /> {pub.nb_subscribers ?? 0} abonné{Number(pub.nb_subscribers) > 1 ? 's' : ''} actif{Number(pub.nb_subscribers) > 1 ? 's' : ''}</span>
                </div>
            )}
            {pub.admin_notes && (
                <p className="pubstat__notes"><strong>Message de l’équipe :</strong> {pub.admin_notes}</p>
            )}
        </section>
    );
}
